/**
 * WikiEditor — Full-page Tiptap editor for wiki articles.
 *
 * Extracted from knowledge.tsx. Composes EditorHeader, EditorToolbar,
 * EditorSidebar, and the Tiptap EditorContent into a cohesive editing view.
 */
import { useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TiptapUnderline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TiptapImage from '@tiptap/extension-image';
import { PanelRight } from 'lucide-react';

import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from 'sonner';
import { htmlToMarkdown } from '@/lib/markdown-utils';
import { Button } from '@/components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from '@/components/ui/sheet';

import { EditorHeader } from './EditorHeader';
import { EditorToolbar } from './EditorToolbar';
import { EditorSidebar, EditorSidebarBody } from './EditorSidebar';
import { LeaveUnsavedDialog } from './LeaveUnsavedDialog';
import { useItemDepartmentsDraft } from '@/components/library/item-departments-field';
import { INCOMPLETE_SCOPE_MESSAGE } from '@/components/users/department-scope-picker';
import { keepsDepartmentsEdit, runArticleSave } from './article-save';
import { saveDocumentTags, tagSetChanged } from '@/components/library/document-tags-field';
import type { ArticleState } from '@argusai/shared';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface KBArticle {
  id: string;
  title: string;
  content: string;
  /** The one article state alphabet (ArticleStateSchema, @argusai/shared). */
  status: ArticleState;
  folderId: string | null;
  parentId: string | null;
  tags: string[];
  authorName: string;
  viewCount: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export interface KBFolder {
  id: string;
  name: string;
  parentId: string | null;
  documentCount?: number;
  isDefault?: boolean;
}

interface WikiEditorProps {
  doc: KBArticle;
  folders: KBFolder[];
  onBack: () => void;
  onSave: (doc: KBArticle) => void;
  onDelete: (id: string) => void;
  onFolderChange: (docId: string, folderId: string) => void;
}

export function WikiEditor({
  doc, folders,
  onBack, onSave, onDelete, onFolderChange,
}: WikiEditorProps) {
  const [title, setTitle] = useState(doc.title);
  const [docTags, setDocTags] = useState<string[]>(doc.tags);
  const [isDirty, setIsDirty] = useState(false);
  const [editorViewMode, setEditorViewMode] = useState<'edit' | 'markdown'>('edit');
  const [isSaving, setIsSaving] = useState(false);
  // Decision 31 (B10, LB6-T8): an article is a document, so its departments
  // are set here, in its metadata, with the Users rail's picker — share:library
  // only (OQ-L6-2). A new article (not yet created) starts with none of its
  // own; they are written once its create answers its id.
  const departments = useItemDepartmentsDraft({
    kind: 'document',
    id: doc.id.startsWith('draft-') ? null : doc.id,
  });
  const hasUnsavedChanges = isDirty || departments.dirty;
  // The id this editor's own save last handed to the page (onSave) — a new
  // article's draft id becoming its real one is not "another item" (BF-01).
  const handedOverId = useRef<string | null>(null);

  // Warn on browser close/refresh if dirty
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Safe back navigation — with unsaved changes, confirmed in an in-app
  // dialog (LeaveUnsavedDialog; no native confirm — LB6T2-REV-06).
  const [confirmLeave, setConfirmLeave] = useState(false);
  const handleSafeBack = () => {
    if (hasUnsavedChanges) {
      setConfirmLeave(true);
      return;
    }
    onBack();
  };

  const editor = useEditor({
    extensions: [
      // #225 — Tiptap StarterKit v3 ships with both Link and Underline
      // bundled. The duplicate explicit registrations below were
      // logging `[tiptap warn]: Duplicate extension names found:
      // ['link', 'underline']` and could destabilise serialization.
      // Disable the StarterKit copies so our explicitly-configured
      // versions (TiptapLink with openOnClick=false; TiptapUnderline
      // for underline-aware styling) own the slot uniquely.
      StarterKit.configure({
        heading: { levels: [1, 2] },
        codeBlock: { HTMLAttributes: { class: 'rounded-md bg-slate-900 text-slate-100 p-4 font-mono text-sm my-4 overflow-x-auto' } },
        bulletList: { HTMLAttributes: { class: 'list-disc ml-6 space-y-1' } },
        orderedList: { HTMLAttributes: { class: 'list-decimal ml-6 space-y-1' } },
        blockquote: { HTMLAttributes: { class: 'border-l-4 border-primary/40 pl-4 py-1 my-4 italic text-muted-foreground bg-muted/20 rounded-r-md' } },
        link: false,
        underline: false,
      }),
      Placeholder.configure({ placeholder: 'Start writing...' }),
      TiptapLink.configure({ openOnClick: false }),
      TaskList.configure({ HTMLAttributes: { class: 'list-none p-0' } }),
      TaskItem.configure({ nested: true, HTMLAttributes: { class: 'flex items-start gap-2 my-1' } }),
      TiptapUnderline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TiptapImage.configure({ inline: false }),
    ],
    content: doc.content || '<p></p>',
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[400px] [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:text-xl [&_h2]:font-semibold',
      },
    },
    onUpdate: () => { setIsDirty(true); },
  });

  // Reset editor if doc changes
  useEffect(() => {
    if (editor && doc.id) {
      editor.commands.setContent(doc.content || '<p></p>');
      setTitle(doc.title);
      setDocTags(doc.tags);
      setIsDirty(false);
    }
    // LB6-T8 review BF-01 — another item opened in this editor does not
    // inherit the last one's unsaved departments; only the editor's own
    // draft-to-saved handover keeps them.
    if (!keepsDepartmentsEdit(doc.id, handedOverId.current)) departments.reset();
    handedOverId.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  const doSave = async (status?: 'draft' | 'published') => {
    if (!editor) return;
    if (isSaving) return; // double-click guard
    const html = editor.getHTML();
    const finalStatus = status ?? doc.status;
    const isDraftNew = doc.id.startsWith('draft-');

    // Issue #89 — backend requires a non-empty title and a folderId.
    // The previous flow POSTed and the user got a generic "Failed to
    // save" toast; we now surface specific, actionable messages.
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) {
      toast.error('Give the article a title before saving.');
      return;
    }
    if (isDraftNew && !doc.folderId) {
      toast.error('Pick a folder in the right sidebar before saving — articles must live in a folder.');
      return;
    }
    // The api refuses "not all departments, and none picked"; say so first.
    if (!departments.complete) {
      toast.error(INCOMPLETE_SCOPE_MESSAGE);
      return;
    }

    setIsSaving(true);
    try {
      const saveStatus =
        finalStatus === 'published' && doc.status !== 'published'
          ? 'draft'
          : finalStatus;
      const publishes = finalStatus === 'published' && doc.status !== 'published';
      const departmentsChanging = departments.dirty;
      // Decision 60 (LB6c-T1): the article's tags go through the one tag door
      // every document shares, only when the set changed — never in the body.
      const tagsChanging = tagSetChanged(doc.tags, docTags);
      const nextTags = [...docTags];

      // LB6-T8 review R1 / BF-02 — the order that fails closed
      // (article-save.ts): an existing article's departments BEFORE its
      // content, so new content is never live under the old, wider scope; a
      // new article's after its create (it has no id before); then its tags
      // (LB6c-T1); all before any publish.
      const outcome = await runArticleSave({
        existingId: isDraftNew ? null : doc.id,
        writeArticle: async () => {
          if (isDraftNew) {
            const created = await apiClient.post<{ id: string; version: number; createdAt: string; updatedAt: string; publishedAt: string | null }>(
              `/wiki/articles`,
              {
                title: trimmedTitle,
                content: html,
                folderId: doc.folderId,
                parentId: doc.parentId,
                status: saveStatus,
              },
            );
            return { id: created.id, version: created.version ?? 1, createdAt: created.createdAt ?? doc.createdAt };
          }
          // Persist title, content, status and folder so edits in the right
          // sidebar (the folder picker) round-trip through the backend
          // instead of evaporating on reload. Tags are their own write below.
          await apiClient.put(`/wiki/articles/${doc.id}`, {
            title: trimmedTitle,
            content: html,
            status: saveStatus,
            folderId: doc.folderId,
          });
          return { id: doc.id, version: doc.version + 1, createdAt: doc.createdAt };
        },
        saveDepartments: (id) => departments.save(id),
        saveTags: tagsChanging ? async (id) => { await saveDocumentTags(id, nextTags); } : null,
        publish: publishes ? async (id) => { await apiClient.post(`/wiki/articles/${id}/publish`); } : null,
      });

      // The article as written, before any publish — with the tags it now
      // carries: the new set once its tags write succeeded, else the stored one.
      // `tagsWritten` is the outcome's, never assumed (LB6cT1-REV-01: a new
      // article whose departments were refused never reached its tags write).
      const asSaved = (saved: { id: string; version: number; createdAt: string }, tagsWritten: boolean): KBArticle => ({
        ...doc,
        id: saved.id,
        title: trimmedTitle,
        content: html,
        tags: tagsWritten ? nextTags : doc.tags,
        status: saveStatus,
        version: saved.version,
        createdAt: saved.createdAt,
        updatedAt: new Date().toISOString(),
        publishedAt: doc.publishedAt,
      });
      const handOver = (next: KBArticle) => {
        handedOverId.current = next.id;
        onSave(next);
        setIsDirty(false);
      };

      if (!outcome.ok) {
        const reason = getApiErrorMessage(outcome.error, 'the request failed');
        if (outcome.failed === 'departments' && outcome.saved === null) {
          // An existing article: nothing was written; the edit stays open.
          toast.error(`Not saved: its departments could not be changed, so the article was not changed either: ${reason}`);
        } else if (outcome.failed === 'departments' && outcome.saved !== null) {
          // A new article: saved as a draft, departments not set, its tags not
          // written (the tags step comes after), not published.
          handOver(asSaved(outcome.saved, outcome.tagsWritten));
          toast.error(`Saved, but its departments were not changed${tagsChanging ? ', its tags were not set' : ''}${publishes ? ' and it was not published' : ''}: ${reason}`);
        } else if (outcome.failed === 'tags') {
          // Saved (with its departments), its tags as they were, not published.
          if (outcome.saved) handOver(asSaved(outcome.saved, outcome.tagsWritten));
          toast.error(`Saved, but its tags were not changed${publishes ? ' and it was not published' : ''}: ${reason}`);
        } else if (outcome.failed === 'article') {
          toast.error(
            outcome.departmentsWritten && departmentsChanging
              ? `Its departments were changed, but the article was not saved: ${reason}`
              : getApiErrorMessage(outcome.error, 'Failed to save'),
          );
        } else {
          // The publish was refused: the article is saved as it was written.
          if (outcome.saved) handOver(asSaved(outcome.saved, outcome.tagsWritten));
          toast.error(getApiErrorMessage(outcome.error, 'Failed to save'));
        }
        return;
      }

      const updated: KBArticle = {
        ...asSaved(outcome.saved, outcome.tagsWritten),
        status: finalStatus,
        publishedAt: finalStatus === 'published' ? new Date().toISOString() : doc.publishedAt,
      };
      handOver(updated);
      toast.success(status === 'published' ? 'Published' : 'Saved');
    } catch (err: any) {
      // Surface the backend's actual error message so the user sees
      // (e.g.) "INVALID_TAGS" or "folderId Required" instead of the
      // opaque "Failed to save" the QA report flagged. For
      // VALIDATION_ERROR responses, getApiErrorMessage returns the
      // api-client's enriched field-level message (PR #327).
      toast.error(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDocTag = (tagId: string) => {
    setDocTags(prev => prev.includes(tagId) ? prev.filter(t => t !== tagId) : [...prev, tagId]);
    setIsDirty(true);
  };

  const handleStatusChange = async (newStatus: 'draft' | 'published') => {
    await doSave(newStatus);
  };

  const handleExport = (format: 'md' | 'pdf') => {
    if (format === 'md') {
      const md = htmlToMarkdown(editor?.getHTML());
      const blob = new Blob([md], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'untitled'}.md`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exported as Markdown');
    } else {
      toast.info('PDF export not implemented yet');
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] bg-background animate-in fade-in duration-200">
      {/* Sticky header */}
      <EditorHeader
        title={title}
        status={doc.status}
        isDirty={hasUnsavedChanges}
        isSaving={isSaving}
        updatedAt={doc.updatedAt}
        viewMode={editorViewMode}
        onBack={handleSafeBack}
        onSave={doSave}
        onDelete={() => onDelete(doc.id)}
        onToggleViewMode={() => setEditorViewMode(prev => prev === 'edit' ? 'markdown' : 'edit')}
        onStatusChange={handleStatusChange}
        onExport={handleExport}
      />

      {/* Toolbar (hidden in markdown preview mode) */}
      {editorViewMode === 'edit' && <EditorToolbar editor={editor} />}

      {/* Editor body + sidebar */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Main content area. #224 — responsive padding: tight on
            narrow viewports so the editor body remains visible after
            the folder rail is hidden via KbSidebar's `hidden md:flex`
            and the metadata sidebar moves into the Sheet drawer
            (below). */}
        <div className="flex-1 min-w-0 overflow-y-auto bg-muted/10">
          {editorViewMode === 'markdown' ? (
            <div className="max-w-4xl mx-auto p-4 sm:p-8">
              <div className="bg-card p-4 sm:p-8 rounded-lg shadow-sm font-mono text-sm whitespace-pre-wrap border h-full overflow-auto">
                {htmlToMarkdown(editor?.getHTML())}
              </div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto w-full pt-6 lg:pt-10 px-4 lg:px-12 bg-card shadow-sm min-h-[calc(100vh-200px)] border-x my-4 rounded-sm">
              <input
                value={title}
                onChange={(e) => { setTitle(e.target.value); setIsDirty(true); }}
                placeholder="Untitled"
                className="text-2xl lg:text-3xl font-bold w-full bg-transparent border-none outline-none placeholder-muted-foreground/30 mb-6"
              />
              <div className="pb-32">
                <EditorContent editor={editor} />
              </div>
            </div>
          )}

          {/* #224 — narrow-viewport metadata trigger. Floats above the
              editor body on `< lg` widths; opens a Sheet containing
              the same EditorSidebarBody so users on tablet/mobile can
              still set folder, tags, and see metadata. Hidden on lg+
              because the inline sidebar takes over there. */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="lg:hidden fixed bottom-4 right-4 z-20 shadow-md gap-1.5"
                aria-label="Open article settings"
                title="Open article settings"
              >
                <PanelRight className="h-3.5 w-3.5" />
                Settings
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] sm:w-[360px] p-0 flex flex-col">
              <SheetHeader className="p-4 border-b">
                <SheetTitle>Article settings</SheetTitle>
                <SheetDescription>
                  Folder, tags, status, and metadata for this article.
                </SheetDescription>
              </SheetHeader>
              <EditorSidebarBody
                embedded
                departments={departments}
                status={doc.status}
                selectedTagIds={docTags}
                folders={folders.map(f => ({ id: f.id, name: f.name }))}
                currentFolderId={doc.folderId}
                onFolderChange={(folderId) => onFolderChange(doc.id, folderId)}
                authorName={doc.authorName}
                createdAt={doc.createdAt}
                version={doc.version}
                onToggleTag={toggleDocTag}
              />
            </SheetContent>
          </Sheet>
        </div>

        {/* Right sidebar — inline at lg+ only (#224). The component
            now self-hides at `< lg`; the Sheet above takes over. */}
        <EditorSidebar
          departments={departments}
          status={doc.status}
          selectedTagIds={docTags}
          folders={folders.map(f => ({ id: f.id, name: f.name }))}
          currentFolderId={doc.folderId}
          onFolderChange={(folderId) => onFolderChange(doc.id, folderId)}
          authorName={doc.authorName}
          createdAt={doc.createdAt}
          version={doc.version}
          onToggleTag={toggleDocTag}
        />
      </div>

      <LeaveUnsavedDialog
        open={confirmLeave}
        onStay={() => setConfirmLeave(false)}
        onLeave={() => {
          setConfirmLeave(false);
          onBack();
        }}
      />
    </div>
  );
}
